import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { apiCall } from "../../../../../../_services/apiCall";
import config from "../../../../../../config/config.json";

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Derives display period label from from_date / to_date strings ("YYYY-MM"). */
const deriveMonth = (fromDate, toDate) => {
  if (!fromDate || !toDate) return '';
  const fromMonth = parseInt(fromDate.split('-')[1], 10) - 1;
  const toMonthRaw = parseInt(toDate.split('-')[1], 10) - 1;
  const endMonth = (toMonthRaw - 1 + 12) % 12;
  if (fromMonth < 0 || fromMonth > 11 || endMonth < 0 || endMonth > 11) return '';
  return fromMonth === endMonth
    ? MONTH_NAMES[fromMonth]
    : `${MONTH_NAMES[fromMonth]}–${MONTH_NAMES[endMonth]}`;
};

/** Reads financial years from localStorage (populated by useFinancialYears hook). */
const getStoredFinancialYears = () => {
  try {
    return JSON.parse(localStorage.getItem('financialYears') || '[]');
  } catch {
    return [];
  }
};

/** Converts financial year value strings (e.g. "2025-26") to numeric IDs. */
const fyValuesToIds = (fyValues = [], allFYs = []) => {
  return fyValues
    .map(val => allFYs.find(fy => fy.financial_year_value === val)?.id)
    .filter(Boolean)
    .map(Number);
};

/** Computes deviation status from actual value vs min/max thresholds. */
const computeStatus = (actual, minVal, maxVal) => {
  let status = 'Normal';
  let deviation = 0;
  if (actual > maxVal) {
    status = 'Above Max';
    deviation = actual - maxVal;
  } else if (actual > 0 && actual < minVal) {
    status = 'Below Min';
    deviation = minVal - actual;
  }
  return { status, deviation, hasThreshold: true };
};

// ─────────────────────────────────────────────────────────────────────────────
// RAW API CALLS
// ─────────────────────────────────────────────────────────────────────────────

// Module-level cache — fetched once per page load, shared across all hook instances.
let _mappingCache = null;
let _mappingPromise = null;

const fetchQuestionMapping = async () => {
  if (_mappingCache) return _mappingCache;
  if (_mappingPromise) return _mappingPromise;

  _mappingPromise = apiCall(
    `${config.POSTLOGIN_API_URL_COMPANY}getQuestionMapping`,
    {}, {}, 'GET', false, false,
  ).then(({ isSuccess, data }) => {
    if (!isSuccess || !data?.data) return {};
    // Build lookup: questionId (string) → { module, category, subCategory }
    // Multiple rows per question_id (tabular sub-questions) — first row wins for taxonomy.
    const map = {};
    data.data.forEach(row => {
      const qid = String(row.question_id);
      if (!map[qid]) {
        map[qid] = {
          module:      row.module       || '',
          category:    row.category     || '',
          subCategory: row.sub_category || '',
        };
      }
    });
    _mappingCache = map;
    return map;
  }).catch(() => ({}));

  return _mappingPromise;
};

const fetchDeviationData = async (financialYearIds, sourceIds) => {
  if (!financialYearIds?.length) return [];
  const params = new URLSearchParams();
  params.set('financialYearIds', financialYearIds.join(','));
  if (sourceIds?.length) params.set('sourceIds', sourceIds.join(','));

  try {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}getDeviationData?${params.toString()}`,
      {},
      {},
      'GET',
      false,
      false,
    );
    if (isSuccess && data?.data) return data.data;
    return [];
  } catch (err) {
    console.error('fetchDeviationData error:', err);
    return [];
  }
};

export const useDeviationRecords = (
  _modules = [],
  filters  = {},
  sources  = [],
  enabled  = true,
) => {
  const [records,             setRecords]             = useState([]);
  const [summaryRows,         setSummaryRows]         = useState([]);
  const [locationSummaryRows, setLocationSummaryRows] = useState([]);
  const [loading,             setLoading]             = useState(false);
  const filtersRef = useRef(null);

  const sourceMap = useMemo(() => {
    const m = {};
    sources.forEach((s) => {
      const label =
        s.unitCode ||
        `${s.location?.area ?? ''} ${s.location?.city ?? ''}`.trim() ||
        String(s.id);
      m[String(s.id)] = label;
    });
    return m;
  }, [sources]);

  const load = useCallback(async () => {
    if (!enabled || !filters.financialYears?.length) {
      setLoading(false);
      setRecords([]);
      setSummaryRows([]);
      setLocationSummaryRows([]);
      return;
    }

    setLoading(true);

    const allFYs       = getStoredFinancialYears();
    const fyIds        = fyValuesToIds(filters.financialYears, allFYs);
    const sourceIds    = (filters.locationIds || []).map(Number).filter(Boolean);
    const monthFilter  = new Set(filters.months || []);

    if (!fyIds.length) {
      setLoading(false);
      setRecords([]);
      setSummaryRows([]);
      setLocationSummaryRows([]);
      return;
    }

    const [rawRows, questionMapping] = await Promise.all([
      fetchDeviationData(fyIds, sourceIds),
      fetchQuestionMapping(),
    ]);

    // ── build a fyId → financial_year_value lookup ──────────────────────────
    const fyIdToValue = Object.fromEntries(
      allFYs.map(fy => [String(fy.id), fy.financial_year_value])
    );

    // ── normalise every raw row ──────────────────────────────────────────────
    const allRecords = [];
    const summaryMap         = new Map();
    const locationSummaryMap = new Map();

    rawRows.forEach((row) => {
      const actual = Number(row.value) || 0;
      const minVal = row.min_trigger_value != null ? Number(row.min_trigger_value) : null;
      const maxVal = row.max_trigger_value != null ? Number(row.max_trigger_value) : null;
      const { status, deviation, hasThreshold } = computeStatus(actual, minVal, maxVal);

      const locId  = String(row.location_id ?? '');
      const loc    = sourceMap[locId] || locId;
      const fy     = fyIdToValue[String(row.financial_year_id)] ?? filters.financialYears?.[0] ?? '';
      const month  = deriveMonth(row.from_date, row.to_date);

      // apply month filter (same as old hook applied displayPeriods filter)
      if (monthFilter.size && !monthFilter.has(month)) return;

      const qMap = questionMapping[String(row.question_id ?? '')] || {};

      const normalized = {
        fy,
        month,
        locId,
        loc,
        kpi:         row.question_title ?? '',
        unit:        row.unit           ?? '',
        module:      qMap.module      || '',
        category:    qMap.category    || '',
        subCategory: qMap.subCategory || '',
        min:         minVal,
        max:         maxVal,
        actual,
        status,
        hasThreshold,
        alertSent:   hasThreshold && status !== 'Normal',
        deviation,
        alertTime:   null,
      };

      allRecords.push(normalized);

      // ── summaryRows: group without location ────────────────────────────────
      const sKey = [fy, month, normalized.kpi, normalized.unit, minVal, maxVal].join('||');
      if (!summaryMap.has(sKey)) {
        summaryMap.set(sKey, { ...normalized, locId: '', loc: '', actual: 0 });
      }
      summaryMap.get(sKey).actual += actual;

      // ── locationSummaryRows: group with location ────────────────────────────
      const lKey = [fy, month, locId, normalized.kpi, normalized.unit, minVal, maxVal].join('||');
      if (!locationSummaryMap.has(lKey)) {
        locationSummaryMap.set(lKey, { ...normalized, actual: 0 });
      }
      locationSummaryMap.get(lKey).actual += actual;
    });

    // re-compute status after summing for summary rows
    const recomputeStatus = (rows) =>
      rows.map((r) => {
        const { status, deviation, hasThreshold } = computeStatus(r.actual, r.min, r.max);
        return { ...r, status, deviation, hasThreshold };
      });

    setRecords(allRecords);
    setSummaryRows(recomputeStatus(Array.from(summaryMap.values())));
    setLocationSummaryRows(recomputeStatus(Array.from(locationSummaryMap.values())));
    setLoading(false);
  }, [filters, sourceMap, enabled]);

  useEffect(() => {
    const key = JSON.stringify({ filters });
    if (filtersRef.current === key) return;
    filtersRef.current = key;
    load();
  }, [load, filters]);

  return { records, summaryRows, locationSummaryRows, loading };
};
